/**
 * @file SearchCustomerOrders.js
 * @version 2.45.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-18
 * @modified 2026-10-30
 *
 * 2.45.0 (2026-10-30, Cam — rep order requests, 29 Oct): "Rep requests" at
 * the top, with a count of the requests waiting (open + opened), opening the
 * office's window (RepOrderRequestsModal — Open / Complete, Create sales order
 * from a request, close one with a reason, its history). Not for a rep. A rep
 * gets "Change" on each phone card and "Request a change" in the row menu
 * (RepChangeRequestModal — a request to the office, never an edit), greyed on
 * an archived or invoiced order.
 *
 * 2.44.0 (2026-10-28, Cam — units clear before November's invoices): the
 * expanded lines' Cost and Sale say what they are per ("£400.00/T",
 * "£10.00/25kg bag" with its figure a tonne, "unit not set" —
 * PriceWithUnit), the PO's cost too; a delivered line with no supplied
 * quantity says "Not recorded — needed before invoicing"; the lines' table
 * export says each price's unit.
 *
 * 2.43.0 (2026-10-28, Cam — "redesign rep mobile view or customer sales
 * orders, as the 'cards' are not looking right on mobile … overlapping text,
 * awkward layout, not-intuitive tap areas triggering effects"): on a rep's
 * phone (useRepMobile) each order is a RepOrderCard instead of a table row
 * restyled by CSS. The card opens the order, "Lines" opens its lines inside
 * the card, "Customer" opens the customer; the status is in words. The
 * desktop table is unchanged.
 *
 * 2.41.0 (2026-10-23, Cam — "Search Customer orders should show customer
 * order lines in the dropdown"): the expanded row is the order's OWN lines
 * (the search's `lines`, services/orderSearchLines), a stock line and a line
 * on a PO alike: Item (code above name) · Description (sale and purchase
 * words with Create Customer Order's icons, read only — LineWordings) ·
 * Supplier (REF: the supplier's reference beneath) · PO# (the PO the line is
 * bought on, a badge that opens it) · Qty and Supplied qty (the line's unit
 * with the tonnes) · Cost (costs.view only, with its additives) · Sale ·
 * VAT £ (%) · Status (the PO's, Stock, or No PO yet). Right-click a line on a
 * PO for that PO, as before.
 * 2.42.0 (2026-10-23, Cam): "Add to another order…" / "Added tonnage…" in the
 * row menus (AddToOrderModal, utils/tonnageAdditions) — a split order's
 * tonnage invoiced on the customer's other order that day — and its pill
 * under the order number ("Added to SO-D…" / "Includes 1.94 T from …"),
 * which opens the other order.
 *
 * 2.38.0 (2026-10-15, Cam): "Refused — redeliver…" / "Edit redelivery…" in
 * the row menus (RedeliverOrderModal, utils/redelivery) and the Redelivery
 * pill beside the order number; the Delivery section of the Status filter
 * offers "Held for Redelivery" and "Redelivery" (the order reads so while a
 * refused delivery waits to go again).
 *
 * 2.37.0 (2026-10-14, Cam): the Status filter has Billed (supplier), Haulage
 * billed (PO-H) and Invoiced (customer) sections, and every section lists all
 * its values. The billed and invoiced icons read the same states (billed_status,
 * draft_invoice_no).
 *
 * 2.36.0 (2026-10-09, Cam): Edit is open on delivered and invoiced orders —
 * corrections to words, item codes, supplier, terms and haulier; the title
 * says what stays as it is (utils/afterDelivery).
 *
 * 2.38.2 (2026-10-20, Cam): the expanded PO lines show the tonnes each line
 * comes to under its unit, and the table export's lines carry their own unit
 * and tonnes (utils/qtyWithTonnes).
 *
 * 2.39.0 (2026-10-21, Cam): the expanded PO lines' Item column is Purchase
 * description — the PO line's own words with the item code a badge to their
 * left (ItemCodeDescription) — and Qty and Supplied show each in the line's
 * own unit with the tonnes beneath (QtyWithTonnes, as Search POs; the Units
 * column went). The row's Item/s column names the item (utils/orderRowSummary
 * 1.1.0).
 * 2.40.0 (2026-10-22, Cam): a third-party order from our stock shows the day
 * the haulier collected it under its delivery date ("Collected 21/10/2026"),
 * and its "Collection / Delivery" action opens before the delivery date
 * (utils/suppliedQty collectsFromStock). The Delivery status filter offers
 * Despatched (collected, before the delivery date).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchCustomerOrders.css';
import Dropdown from './Dropdown';
import NonInventoryBadge from './NonInventoryBadge';
import Input from './Input';
import DateRangeFilter from './DateRangeFilter';
import ProductRangeFilter, { productRangeParam } from './ProductRangeFilter';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import CustomerOrderModal from './CustomerOrderModal';
import PurchaseOrderModal from './PurchaseOrderModal';
import { invoiceBlockFor, startInvoiceFromOrder } from '../utils/invoiceFromOrder';
import AddSuppliedQuantityModal from './AddSuppliedQuantityModal';
import SplitOrderModal from './SplitOrderModal';
import DivertOrderFlow from './DivertOrderFlow';
import DivertedBadge from './DivertedBadge';
import RedeliveryBadge from './RedeliveryBadge';
import RedeliverOrderModal from './RedeliverOrderModal';
import { redeliverMenuAction } from '../utils/redeliveryActions';
import { addToOrderMenuAction } from '../utils/tonnageAdditionActions';
import AddToOrderModal from './AddToOrderModal';
import TonnageAdditionBadge from './TonnageAdditionBadge';
import { DELIVERY_STATUS_HELD, DELIVERY_STATUS_REDELIVERY } from '../utils/redelivery';
import { divertBlockForOrder, splitBlockForOrder } from '../utils/orderHub';
import DeleteOrderModal from './DeleteOrderModal';
import ArchiveOrderFlow, { patchOrderArchive, orderArchiveOutcome } from './ArchiveOrderFlow';
import ReactDOM from 'react-dom';
import { useSlideOutButton } from './SlideOutButton';
import CustomIcon from './CustomIcon';
import ContactModal from './ContactModal';
import { useDragSelect } from '../hooks/useDragSelect';
import TickCheckbox from './TickCheckbox';
import usePersistedState from '../hooks/usePersistedState';
import RowActionsMenu from './RowActionsMenu';
import SendToSupplierFlow, { poSentRowClass } from './SendToSupplierFlow';
import useResizableColumns from '../hooks/useResizableColumns';
import './ResizableColumns.css';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import SortOptionsMenu, { useSortMenu } from './SortOptionsMenu';
import ColumnFilterNote from './ColumnFilterNote';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import ExportTableButton from './ExportTableButton';
import TableContextMenu from './TableContextMenu';
import RepMobileFilterReset from './RepMobileFilterReset';
import RepOrderCard from './RepOrderCard';
import RepOrderRequestsModal from './RepOrderRequestsModal';
import RepChangeRequestModal from './RepChangeRequestModal';
import { repRequestsApi } from '../utils/repOrderRequestApi';
import { NOT_READY_TEXT as REP_REQUESTS_NOT_READY, orderAmendBlock } from '../utils/repOrderRequests';
import './RepOrderRequests.css';
import useRepMobile from '../hooks/useRepMobile';
import { useReveal, revealFilters, revealInSearch } from '../utils/revealInSearch';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { quantityWithTonnesText, qtyFactsOf } from '../utils/qtyWithTonnes';
import QtyWithTonnes from './QtyWithTonnes';
import PriceWithUnit from './PriceWithUnit';
import { withPerUnit, priceFactsOf } from '../utils/unitPriceLabel';
import ItemCodeDescription from './ItemCodeDescription';
import LineWordings from './LineWordings';
import { useAccess } from '../hooks/useAccess';
import { formatVatWithRate } from '../utils/taxTypes';
import { poStatusLabel, poStatusBadgeClass } from '../utils/poStatus';
import { addressLine1 } from '../utils/formatAddress';
import { STATUS_COLORS, billedIconForOrder, pipelineIconsForOrder } from '../utils/orderStatusIcons';
import { suppliersOf, itemsOf } from '../utils/orderRowSummary';
import { suppliedQtyBlock, collectsFromStock, collectedText } from '../utils/suppliedQty';
import { orderEditTitle } from '../utils/afterDelivery';

// The most rows an export will fetch for "all results" — the export route's own cap.
const EXPORT_ALL_LIMIT = 1000;

/**
 * The Status header's sections, each with every value it can hold — the
 * order statuses (services/orderStatusService), Supplied Qty, and Billed /
 * Haulage billed / Invoiced (server utils/orderStatusFilters).
 */
export const STATUS_SECTIONS = [
  { key: 'po_status', label: 'Purchase orders', values: ['No POs Linked', 'POs Partially Linked', 'POs Linked'] },
  { key: 'sent_status', label: 'Sent to supplier', values: ['No POs Sent', 'POs Partially Sent', 'POs Sent'] },
  { key: 'confirmed_status', label: 'Confirmed', values: ['No POs Confirmed', 'POs Partially Confirmed', 'POs Confirmed'] },
  { key: 'transport_status', label: 'Transport', values: ['Not Planned', 'Partially Planned', 'Transport Planned'] },
  // Held for Redelivery / Redelivery: refused on delivery and waiting to go
  // again (utils/redelivery; the server reads them, the stored status stays Delivered).
  { key: 'delivery_status', label: 'Delivery', values: ['Not Despatched', 'Despatched', 'Partially Despatched', 'In-Transit', 'Partially Delivered', 'Delivered', DELIVERY_STATUS_HELD, DELIVERY_STATUS_REDELIVERY] },
  { key: 'supplied_qty', label: 'Supplied Qty', values: ['Recorded', 'To record'] },
  { key: 'billed_status', label: 'Billed (supplier)', values: ['Billed', 'Partially Billed', 'Not Billed', 'No POs'] },
  { key: 'haulage_billed', label: 'Haulage billed (PO-H)', values: ['Billed', 'Partially Billed', 'Not Billed'] },
  { key: 'invoiced_status', label: 'Invoiced (customer)', values: ['Invoiced', 'Draft Invoice', 'Not Invoiced'] },
  { key: 'is_priced', label: 'Priced', values: ['Not Priced', 'Partially Priced', 'Priced'] }
];

/**
 * Columns filterable from their header. Keys must match the whitelist in the
 * customer-orders search route; an unknown key narrows nothing.
 */
const COLUMN_FILTERS = {
  company_name: { label: 'Customer' },
  /* Both cells hold a LIST — an order can be split across two mills and carry
     four products — so the server side of these is an array entry, and ticking
     one value keeps every order that has it among however many others. */
  supplier_names: { label: 'Supplier/s' },
  item_names: { label: 'Item/s' },
  /* Third-party haulage or not — two values, so one tick means "only" and the
     other "exclude". Lives on the PO-H # header, the column it describes. */
  third_party: { label: 'PO-H #' },
  /*
   * The Status cell is a row of icons saying six independent things about an
   * order — whether it is on a purchase order, sent, confirmed, planned,
   * delivered and priced. Its header's menu is those six, stacked and
   * collapsible, first one open. Ordered as the icons are, and as they happen:
   * an order is raised, sent, confirmed, planned, delivered, then priced.
   *
   * Supplied Qty (Cam, 2026-10-08) sits after Delivery, where it happens:
   * "Recorded" — every line has its supplied quantity; "To record" — it is
   * due (as Add / Update Supplied Qty allows it) and a line has none. An order
   * not due yet is neither (utils/suppliedQtyFilter.js on the server).
   *
   * Billed, Haulage billed and Invoiced (Cam, 2026-10-14 — "so users can
   * check for orders that haven't been billed or haven't been invoiced"),
   * after Supplied Qty as their icons sit (utils/orderStatusFilters.js on the
   * server). Every section lists all its values (`values`), a status no order
   * is in today with 0, so it can still be looked for.
   */
  status: {
    label: 'Status',
    sections: STATUS_SECTIONS
  }
};

/*
 * The PO-H # cell. A third-party order shows its haulage order (click to open,
 * amber until sent) and the haulier; one without a PO-H yet offers to raise
 * it. Any other order is left blank.
 */
function HaulageCell({ order, onOpen, onRaise }) {
  const pos = Array.isArray(order.haulage_pos) ? order.haulage_pos : [];
  if (!pos.length && !order.haulier_account_id) return null;
  return (
    <span className="stacked-cell">
      {pos.length > 0 ? pos.map(po => (
        <span
          key={po.id}
          className={`linked-po-link po-h-link${po.sent_to_contact ? '' : ' is-unsent'}`}
          title={`Open ${po.purchase_order_number}${po.sent_to_contact ? '' : ' — not yet sent to the haulier'}`}
          onClick={(e) => { e.stopPropagation(); onOpen(po); }}
        >
          {po.purchase_order_number}
        </span>
      )) : (
        <button
          type="button"
          className="po-h-raise"
          data-write-action
          title="Third-party delivery with no haulage order yet — raise the PO-H"
          onClick={(e) => { e.stopPropagation(); onRaise(); }}
        >
          <span className="material-symbols-rounded">add</span>
          Raise PO-H
        </button>
      )}
      {order.haulier_name && <span className="po-h-haulier">{order.haulier_name}</span>}
    </span>
  );
}

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', po_status: 'all', sent_status: 'all', confirmed_status: 'all',
  transport_status: 'all', delivery_status: 'all', priced: 'all',
  supplier: 'all', productRange: [], archived: false,
  dateType: 'delivery', dateFrom: '', dateTo: '', datePreset: 'all-time',
  page: 1, limit: '20',
  includeAdditives: true
};

function SearchCustomerOrders({ user, onNavigate }) {
  /*
   * Arriving from a modal's "Find in …" (utils/revealInSearch.js): the
   * reference seeds the filters and the page goes back to 1, both in the FIRST
   * render's state, so the mount search is the one that was asked for. Read
   * before the usePersistedState calls below, which it seeds.
   */
  const reveal = useReveal('search-customer-orders');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = usePersistedState('searchSOs.soModalId', null);
  // Xero PO id string (incl. TEMP-/LOCAL- placeholders) of the linked PO whose
  // modal is open — never the local integer id, which PurchaseOrderModal can't
  // resolve. See openPurchaseOrder below.
  const [poModalXeroId, setPoModalXeroId] = usePersistedState('searchSOs.poModalId', null);

  // Drag-to-resize columns (12 cols: checkbox, expand, order #, PO-H #,
  // customer, supplier, items, order date, delivery date, total, status,
  // actions). v3: PO-H # was inserted after Order #.
  //
  // The page key carries a version because widths persist by column INDEX.
  // Supplier and Items were inserted mid-table, so a width saved against the
  // old eight-column layout would land on the wrong column — the key change
  // drops those rather than applying them somewhere they were never set.
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('searchCustomerOrders.v3', 12);

  const [pagination, setPagination] = usePersistedState('searchSOs.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }, { seed: reveal && { page: 1 } });

  // merge: a slot saved before a filter key existed (or by the old cross-search,
  // which wrote just { search } here) would otherwise come back missing keys
  // the screen believes it owns. seed: a "Find in …" handoff (see above).
  const [filters, setFilters] = usePersistedState('searchSOs.filters', {
    search: '',
    po_status: 'all',
    sent_status: 'all',
    confirmed_status: 'all',
    transport_status: 'all',
    delivery_status: 'all',
    priced: 'all',
    supplier: 'all',
    productRange: [],
    dateType: 'delivery',
    dateFrom: '',
    dateTo: '',
    datePreset: 'all-time',
    archived: false,
    page: 1,
    limit: '20',
    includeAdditives: true
  }, { merge: true, seed: revealFilters(reveal) });

  const [sortField, setSortField] = usePersistedState('searchSOs.sortField', 'order_date');
  const [sortDirection, setSortDirection] = usePersistedState('searchSOs.sortDirection', 'desc');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [suppliedQtyOrderId, setSuppliedQtyOrderId] = useState(null);
  const [splitOrderId, setSplitOrderId] = useState(null);
  // Divert: the whole order to another customer (DivertOrderFlow).
  const [divertOrderId, setDivertOrderId] = useState(null);
  // Add to another order… / Added tonnage… (AddToOrderModal).
  const [addToOrderId, setAddToOrderId] = useState(null);
  // Refused — redeliver… / Edit redelivery… (RedeliverOrderModal).
  const [redeliverOrderId, setRedeliverOrderId] = useState(null);
  // Delete-order-with-POs checklist dialog: { order, linkedPOs } or null.
  const [deleteOrderData, setDeleteOrderData] = useState(null);
  // The order being archived (ArchiveOrderFlow — shared with the planning boards).
  const [archiveOrderFlow, setArchiveOrderFlow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [contactModalId, setContactModalId] = usePersistedState('searchSOs.contactModalId', null);
  const isInitialMount = useRef(true);
  // Distinct values per filterable column across the whole result set.
  const [facets, setFacets] = useState({});

  // Bulk selection state
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);
  const [bulkActionsClosing, setBulkActionsClosing] = useState(false);

  // Right-click a header to narrow by the values on this page.
  const sortMenu = useSortMenu();

  const cols = useColumnFilters(orders, COLUMN_FILTERS, {
    facets,
    storageKey: 'searchOrders.columnFilters',
    onChange: () => setPagination(prev => ({ ...prev, page: 1 }))
  });

  /**
   * The rows on screen with each order's lines beneath.
   *
   * The table itself has no expandable rows, but the lines ARE the order —
   * a printed list of order numbers and totals answers almost nothing on its
   * own — so the export fetches them.
   */
  const buildExport = async ({ scope = 'page' } = {}) => {
    // "All results" fetches past the page on screen with the same filters and
    // sort, up to the export's own row limit.
    const onPage = cols.rows;
    const totalAvailable = Math.max(Number(pagination.total) || 0, onPage.length);
    let source = onPage;
    if (scope === 'all' && totalAvailable > onPage.length) {
      try {
        const res = await fetch(
          `${API_URL}/api/orders/customer-orders/search?${searchParamsFor(1, EXPORT_ALL_LIMIT)}`,
          { credentials: 'include' });
        const data = await res.json();
        if (Array.isArray(data.orders)) source = data.orders;
      } catch (error) {
        console.error('Could not fetch every result for the export:', error);
      }
    }

    let lines = {};
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/lines/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: source.map((o) => o.id) })
      });
      const data = await response.json();
      lines = data.lines || {};
    } catch (error) {
      console.error('Could not load order lines for the export:', error);
    }

    const detailColumns = [
      { key: 'code', label: 'Product code', width: '14%' },
      { key: 'product', label: 'Product', width: '34%' },
      { key: 'supplier', label: 'Supplier', width: '18%' },
      { key: 'qty', label: 'Qty', width: '9%', numeric: true },
      { key: 'unit', label: 'Unit price', width: '12%', numeric: true },
      { key: 'lineTotal', label: 'Line total', width: '13%', numeric: true }
    ];

    const criteria = [];
    if (filters.search) criteria.push({ label: 'Search', value: filters.search });
    if (filters.dateFrom || filters.dateTo) {
      criteria.push({
        label: filters.dateType === 'created' ? 'Created' : 'Delivery',
        value: `${filters.dateFrom || 'any'} to ${filters.dateTo || 'any'}`
      });
    }
    for (const [key, values] of Object.entries(cols.selected)) {
      if (!Array.isArray(values)) continue;
      criteria.push({ label: cols.definitions[key]?.label || key, value: values.join(', ') || 'none' });
    }

    return {
      title: 'Customer Orders',
      kicker: 'ORDERS',
      rowNoun: source.length === 1 ? 'order' : 'orders',
      filename: `customer-orders-${new Date().toISOString().slice(0, 10)}`,
      landscape: true,
      columns: [
        { key: 'order', label: 'Order #', width: '11%' },
        { key: 'poh', label: 'PO-H #', width: '9%' },
        { key: 'customer', label: 'Customer', width: '22%' },
        { key: 'created', label: 'Created', width: '9%' },
        { key: 'delivery', label: 'Delivery', width: '9%' },
        { key: 'total', label: 'Total', width: '11%', numeric: true },
        { key: 'pos', label: 'Linked POs', width: '15%' },
        { key: 'status', label: 'Status', width: '15%' }
      ],
      rows: source.map((order) => ({
        order: order.order_number || '',
        poh: (order.haulage_pos || []).map((p) => p.purchase_order_number).join(', ')
          || (order.haulier_account_id ? 'Not raised' : ''),
        customer: [order.account_no, order.company_name].filter(Boolean).join(' · '),
        created: formatDate(order.order_date),
        delivery: formatDate(order.delivery_date),
        total: formatCurrency(order.total_amount),
        pos: (order.linked_pos || []).map((p) => p.purchase_order_number).join(', '),
        // The status column is a row of six icons on screen, which prints as
        // nothing useful. Spelled out from the same fields the icons read.
        status: [
          order.po_status,
          order.sent_status,
          order.confirmed_status,
          order.transport_status,
          order.delivery_status,
          order.invoiced_status,
          order.is_priced
        ].filter((v) => v && v !== 'No' && v !== 'None').join(', ') || '—',
        detail: {
          columns: detailColumns,
          emptyLabel: 'No lines on this order',
          rows: (lines[order.id] || []).map((line) => ({
            code: line.product_code || '',
            product: [line.product_name, line.additive_name && `+ ${line.additive_name}`]
              .filter(Boolean).join(' '),
            supplier: line.supplier_name || '',
            // Its own unit and the tonnes it comes to (utils/qtyWithTonnes).
            qty: quantityWithTonnesText(line.quantity, qtyFactsOf(line)),
            unit: line.unit_price !== undefined && line.unit_price !== null
              ? withPerUnit(formatPrice(line.unit_price), priceFactsOf(line)) : '',
            lineTotal: line.line_total !== undefined && line.line_total !== null
              ? formatCurrency(line.line_total) : ''
          }))
        }
      })),
      totals: [{
        order: `${source.length} orders`,
        total: formatCurrency(source.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0))
      }],
      criteria,
      available: { page: onPage.length, all: totalAvailable },
      scopeNote: scope === 'all' && totalAvailable > EXPORT_ALL_LIMIT
        ? `First ${EXPORT_ALL_LIMIT.toLocaleString('en-GB')} of ${totalAvailable.toLocaleString('en-GB')} results`
        : ''
    };
  };

  // How much of the table is still off to the right, for the cue.
  const [tableScrollRef, tableScroll] = useScrollAffordance([]);

  const ctxMenu = useTableContextMenu({
    rows: orders,
    selectedIds: selectedOrders,
    getRowId: (o) => o.id
  });

  /*
   * The right-click menu for a row of the EXPANDED table, which is a purchase
   * order line rather than a customer order. Its own instance because the two
   * tables offer different actions against different things — sharing one
   * would mean every action deciding which kind of row it had been handed.
   */
  const poLineCtxMenu = useTableContextMenu({ rows: [], getRowId: (r) => r?.key });

  // ── Expandable rows ────────────────────────────────────────────────────
  //
  // Nothing is fetched on expand: the linked POs and their lines arrive with
  // the row, because the Supplier and Items columns on the main row are built
  // from the same lines. Expanding is therefore instant, and so is Expand All.
  const [expandedRows, setExpandedRows] = useState(new Set());

  /*
   * Expand All is a mode, not a one-off action — the same decision Search
   * Purchase Orders made. Expanding the rows on screen and nothing more means
   * paging or re-searching silently collapses everything again, which reads as
   * the button having failed.
   */
  const [expandAllMode, setExpandAllMode] = usePersistedState('searchSOs.expandAll', false);

  const toggleRowExpand = (orderId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  };

  const handleExpandAll = () => {
    if (expandAllMode) {
      setExpandAllMode(false);
      setExpandedRows(new Set());
      return;
    }
    setExpandAllMode(true);
    setExpandedRows(new Set(orders.map(o => o.id)));
  };

  // A new page (or a new search) while the mode is on opens its rows too.
  useEffect(() => {
    if (!expandAllMode) return;
    setExpandedRows(new Set(orders.map(o => o.id)));
  }, [orders, expandAllMode]);

  /*
   * The expanded table's rows: the order's OWN lines (Cam, 23 Oct 2026 —
   * "Search Customer orders should show customer order lines in the
   * dropdown"), a stock line and a line on a PO alike. A line on a PO carries
   * that PO from the row's linked_pos where it is there (what Send to Supplier
   * and the unsent marker read), else the server's own copy on the line.
   * null = the server could not read them.
   */
  const orderLineRowsOf = useCallback((order) => {
    if (!Array.isArray(order.lines)) return null;
    const linked = new Map((order.linked_pos || []).map(po => [Number(po.id), po]));
    return order.lines.map(line => ({
      key: line.id,
      line,
      po: line.po ? (linked.get(Number(line.po.id)) || line.po) : null
    }));
  }, []);

  // The Cost column: purchase costs, for costs.view only (the server leaves
  // them off the lines for everyone else).
  const { can, canWrite, badge } = useAccess();
  const seeCosts = can('costs.view');

  // Rep order requests (Cam, 29 Oct 2026). The office: "Rep requests" with
  // the count waiting. A rep: "Request a change" on their orders.
  const isRep = badge === 'REP';
  const canSendRequests = isRep && canWrite('rep_order_requests.send');
  const [showRepRequests, setShowRepRequests] = useState(false);
  const [repRequestCount, setRepRequestCount] = useState({ ready: true, count: 0 });
  const [changeRequestOrderId, setChangeRequestOrderId] = useState(null);
  const loadRepRequestCount = useCallback(() => {
    if (isRep) return;
    repRequestsApi.count()
      .then(d => setRepRequestCount({ ready: d.ready !== false, count: d.count || 0 }))
      .catch(() => {});
  }, [isRep]);
  useEffect(() => {
    loadRepRequestCount();
    if (isRep) return undefined;
    const t = setInterval(loadRepRequestCount, 60000);
    return () => clearInterval(t);
  }, [isRep, loadRepRequestCount]);

  // A rep's phone gets a card per order (RepOrderCard) instead of the table.
  const repMobile = useRepMobile();

  // Slideout buttons - re-measure when bulk actions bar appears
  useSlideOutButton([bulkActionsVisible, selectedOrderId, poModalXeroId, suppliedQtyOrderId, splitOrderId, contactModalId]);

  useEffect(() => {
    // Back to the page you were on, not page 1: the filters are restored from
    // the nav store, so the page number should be too.
    searchOrders(pagination.page || 1);
    // Mark initial mount as complete after a short delay to let state settle
    setTimeout(() => {
      isInitialMount.current = false;
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search on filter change (skip during initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    const timeoutId = setTimeout(() => {
      searchOrders(1);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A column filter is part of the query, so changing one re-runs the search
  // the same way changing a filter above the table does.
  useEffect(() => {
    if (isInitialMount.current) return;
    searchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.queryValue]);

  // Clear selection when search results change
  useEffect(() => {
    setSelectedOrders(new Set());
  }, [orders]);

  // Handle bulk actions bar animation
  useEffect(() => {
    if (selectedOrders.size > 0) {
      if (!bulkActionsVisible || bulkActionsClosing) {
        setBulkActionsVisible(true);
        setBulkActionsClosing(false);
      }
    } else if (selectedOrders.size === 0 && bulkActionsVisible && !bulkActionsClosing) {
      setBulkActionsClosing(true);
      // No clearTimeout cleanup — setBulkActionsClosing(true) re-runs this effect,
      // and cancelling the timer would leave the bar stuck visible.
      setTimeout(() => {
        setBulkActionsVisible(false);
        setBulkActionsClosing(false);
      }, 250);
    }
  }, [selectedOrders.size, bulkActionsVisible, bulkActionsClosing]);

  // The search's query string for a page of results — shared by the screen and
  // the export's "all results", so the two cannot filter differently.
  const searchParamsFor = (page, limit) => {
      const cleanFilters = {
        search: filters.search || '',
        po_status: filters.po_status === 'all' ? '' : filters.po_status,
        sent_status: filters.sent_status === 'all' ? '' : filters.sent_status,
        confirmed_status: filters.confirmed_status === 'all' ? '' : filters.confirmed_status,
        transport_status: filters.transport_status === 'all' ? '' : filters.transport_status,
        delivery_status: filters.delivery_status === 'all' ? '' : filters.delivery_status,
        priced: filters.priced === 'all' ? '' : filters.priced,
        // Guarded with `||` rather than a bare `=== 'all'` check: a persisted
        // filters object saved before these two keys existed has them undefined,
        // which URLSearchParams would otherwise serialise as the string
        // "undefined" and the server would treat as a real filter value.
        supplier_id: (filters.supplier && filters.supplier !== 'all') ? filters.supplier : '',
        productRange: productRangeParam(filters.productRange),
        archived: filters.archived ? 'true' : 'false',
        dateType: filters.dateType || 'delivery',
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        limit: limit || filters.limit || 50,
        includeAdditives: filters.includeAdditives ? 'true' : 'false'
      };

      const queryParams = new URLSearchParams({
        ...cleanFilters,
        page: page,
        sortField: sortField,
        sortDirection: sortDirection
      });

      // Column-header filters travel with the search, so they narrow the whole
      // result set and survive paginating and re-searching.
      if (cols.queryValue) queryParams.set('columnFilters', cols.queryValue);
      return queryParams;
  };

  /*
   * Only the NEWEST search may write the results. Each keystroke fires one,
   * 300ms apart, and they do not come back in order: a slow "burc" landing
   * after a quick "burcombe" put the wrong rows under the text in the box.
   */
  const latest = useLatestRequest();

  const searchOrders = async (page = 1) => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const queryParams = searchParamsFor(page);
      const response = await fetch(`${API_URL}/api/orders/customer-orders/search?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search

      if (data.success) {
        setOrders(data.orders || []);
        if (data.facets) setFacets(data.facets);
        setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
      }
    } catch (error) {
      if (!latest.isLatest(seq)) return;
      console.error('Search failed:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to search orders: ' + error.message, type: 'error' });
    }
    if (latest.isLatest(seq)) setLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchOrders(1);
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSortField('order_date');
    setSortDirection('desc');
    // Column filters are filters; leaving them on after a button labelled
    // "clear all filters" is how a search gets reported as broken.
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS);
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    if (isInitialMount.current) return;
    if (pagination.page > 0) {
      searchOrders(pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      searchOrders(newPage);
    }
  };

  const handleAddOrder = () => {
    if (onNavigate) {
      onNavigate('create-customer-order');
    }
  };

  const handleViewOrder = (orderId) => {
    setSelectedOrderId(orderId);
  };

  // PurchaseOrderModal resolves its prop against po.xero_purchase_order_id, so
  // it must be handed the Xero id STRING (TEMP-…/LOCAL-… included for POs never
  // pushed to Xero). The local integer id silently 404s as "not found".
  const openPurchaseOrder = (linkedPo) => {
    if (!linkedPo?.xero_purchase_order_id) return;
    setPoModalXeroId(linkedPo.xero_purchase_order_id);
  };

  /*
   * Search Purchase Orders' filters for "show me these POs": every filter that
   * could hide one cleared. 'all' would apply the server's default
   * DELETED/VOIDED exclusion — a PO we send the user to look at may be
   * deleted, so show everything.
   */
  const PO_SEARCH_HANDOFF_FILTERS = {
    status: 'all_inc_deleted',
    supplier: 'all',
    productRange: [],
    dateType: 'delivery',
    dateFrom: '',
    dateTo: '',
    datePreset: 'all-time',
    page: 1,
    limit: '20',
    includeAdditives: true,
    // Archived POs too: the order's POs are shown wherever they are.
    activeOnly: false
  };

  // Hand the user to Search Purchase Orders pre-filtered to one PO, so they can
  // take any action available there (utils/revealInSearch.js).
  const openPOInSearch = (po) => {
    if (!po?.purchaseOrderNumber || !onNavigate) return;
    revealInSearch(onNavigate, 'search-purchase-orders', po.purchaseOrderNumber,
      () => setPoModalXeroId(null),
      { tab: 'orders', filters: PO_SEARCH_HANDOFF_FILTERS });
  };

  /*
   * The multi-supplier way out: Search Purchase Orders, filtered to this order.
   *
   * Searching the ORDER number finds all of them — a PO raised from a customer
   * order carries that order's number as its reference, which the PO search
   * already matches on — so no new filter is needed to get there.
   */
  const openOrderInPOSearch = (orderNumber) => {
    if (!orderNumber || !onNavigate) return;
    revealInSearch(onNavigate, 'search-purchase-orders', orderNumber,
      () => setSendFlowOrder(null),
      { tab: 'orders', filters: PO_SEARCH_HANDOFF_FILTERS });
  };

  const handleEditOrder = (orderId) => {
    if (onNavigate) {
      sessionStorage.setItem('editOrderId', orderId);
      onNavigate('create-customer-order');
    }
  };

  // A duplicate is a NEW order pre-filled from an old one. The Create screen
  // does the work; all this does is say which order to copy. Allowed on any
  // order, invoiced ones included — repeat business is the case it is for.
  const handleDuplicateOrder = (orderId) => {
    if (onNavigate) {
      sessionStorage.setItem('duplicateOrderId', orderId);
      onNavigate('create-customer-order');
    }
  };

  const handleCreatePO = async (order) => {
    setConfirmDialog({
      isOpen: true,
      message: `Generate purchase order(s) for ${order.order_number}? This will create PO(s) for each supplier in the order.`,
      onConfirm: () => confirmCreatePO(order.id)
    });
  };

  const confirmCreatePO = async (orderId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/create-po`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        let message = data.message;
        if (data.purchase_orders && data.purchase_orders.length > 0) {
          const poList = data.purchase_orders.map(po =>
            `${po.number} (${po.supplier})`
          ).join(', ');
          message = `Purchase order${data.purchase_orders.length > 1 ? 's' : ''} created: ${poList}`;
        }

        setAlertDialog({ isOpen: true, message, type: 'success' });
        searchOrders(pagination.page);
      } else {
        throw new Error(data.error || 'Failed to create purchase order(s)');
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to create PO: ${error.message}`, type: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteOrder = async (order) => {
    const isChildSplit = order.is_split;

    if (order.invoiced_status === 'Invoiced') {
      setAlertDialog({ isOpen: true, message: 'Invoiced orders cannot be deleted', type: 'warning' });
      return;
    }

    // Child split orders restore quantities to the parent — simple confirm.
    if (isChildSplit) {
      setConfirmDialog({
        isOpen: true,
        message: `Delete split order ${order.order_number}? Its quantities will be restored to the parent order.`,
        onConfirm: () => confirmDeleteOrder(order.id)
      });
      return;
    }

    // Regular order with no linked POs — simple confirm.
    if (order.po_status === 'No POs Linked') {
      setConfirmDialog({
        isOpen: true,
        message: `Are you sure you want to delete order ${order.order_number}? This action cannot be undone.`,
        onConfirm: () => confirmDeleteOrder(order.id)
      });
      return;
    }

    // Regular order with linked POs — open the checklist dialog so the user can
    // pick which POs to delete alongside the order.
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${order.id}`, { credentials: 'include' });
      const data = await res.json();
      const linkedPOs = data.purchase_orders || [];
      if (linkedPOs.length === 0) {
        setConfirmDialog({
          isOpen: true,
          message: `Are you sure you want to delete order ${order.order_number}? This action cannot be undone.`,
          onConfirm: () => confirmDeleteOrder(order.id)
        });
      } else {
        setDeleteOrderData({ order, linkedPOs });
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to load linked POs: ${error.message}`, type: 'error' });
    }
  };

  const confirmDeleteOrder = async (orderId, deletePoIds = null) => {
    setLoading(true);
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: deletePoIds ? JSON.stringify({ deletePoIds }) : undefined
      });

      const data = await response.json();

      if (data.success) {
        setDeleteOrderData(null);
        setAlertDialog({ isOpen: true, message: data.message || 'Order deleted successfully', type: 'success' });
        searchOrders(pagination.page);
      } else {
        throw new Error(data.error || 'Failed to delete order');
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to delete order: ${error.message}`, type: 'error' });
    }
    setLoading(false);
    setDeleteLoading(false);
  };

  // ============================================================
  // Bulk Selection
  // ============================================================

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedOrders(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === orders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map(o => o.id)));
    }
  };

  // ============================================================
  // Bulk Actions
  // ============================================================

  const handleBulkDelete = () => {
    const selectedList = orders.filter(o => selectedOrders.has(o.id));
    const nonDeletable = selectedList.filter(o => !canDeleteOrder(o));

    if (nonDeletable.length > 0) {
      setAlertDialog({
        isOpen: true,
        message: `${nonDeletable.length} of the selected orders cannot be deleted — a PO has been sent or confirmed, or the order has been delivered, billed or invoiced. Please deselect these items.`,
        type: 'warning'
      });
      return;
    }

    const withPOs = selectedList.filter(o => o.po_status && o.po_status !== 'No POs Linked').length;

    setConfirmDialog({
      isOpen: true,
      message: `Are you sure you want to delete ${selectedOrders.size} order${selectedOrders.size !== 1 ? 's' : ''}?${withPOs > 0 ? ` Any linked draft purchase orders will also be deleted.` : ''} This action cannot be undone.`,
      onConfirm: confirmBulkDelete
    });
  };

  const confirmBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/bulk/delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ orderIds: Array.from(selectedOrders) })
      });
      const data = await response.json();

      if (data.success) {
        setAlertDialog({ isOpen: true, message: data.message, type: 'success' });
        setSelectedOrders(new Set());
        searchOrders(pagination.page);
      } else {
        throw new Error(data.error || 'Failed to delete orders');
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to delete orders: ${error.message}`, type: 'error' });
    }
    setBulkActionLoading(false);
  };

  const handleBulkSuppliedQty = () => {
    const selectedList = orders.filter(o => selectedOrders.has(o.id));
    const ineligible = selectedList.filter(o => {
      const s = o.status?.toLowerCase();
      return s === 'created' || s === 'invoiced';
    });

    if (ineligible.length > 0) {
      setAlertDialog({
        isOpen: true,
        message: `${ineligible.length} of the selected orders cannot have supplied quantities set (status is "Created" or "Invoiced"). Please deselect these orders first.`,
        type: 'warning'
      });
      return;
    }

    setConfirmDialog({
      isOpen: true,
      message: `Set supplied quantity to match ordered quantity for all lines in ${selectedOrders.size} order${selectedOrders.size !== 1 ? 's' : ''}?`,
      onConfirm: confirmBulkSuppliedQty
    });
  };

  const confirmBulkSuppliedQty = async () => {
    setBulkActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/bulk/supplied-quantity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ orderIds: Array.from(selectedOrders) })
      });
      const data = await response.json();

      if (data.success) {
        setAlertDialog({ isOpen: true, message: data.message, type: 'success' });
        setSelectedOrders(new Set());
        searchOrders(pagination.page);
      } else {
        throw new Error(data.error || 'Failed to update supplied quantities');
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to update: ${error.message}`, type: 'error' });
    }
    setBulkActionLoading(false);
  };

  const handleBulkGenerateInvoice = () => {
    const selectedList = orders.filter(o => selectedOrders.has(o.id));
    const deliveredCount = selectedList.filter(o => o.status?.toLowerCase() === 'delivered').length;

    setConfirmDialog({
      isOpen: true,
      message: `Generate invoices for ${selectedOrders.size} selected order${selectedOrders.size !== 1 ? 's' : ''}? Only orders with "Delivered" status will be processed (${deliveredCount} eligible).`,
      onConfirm: confirmBulkGenerateInvoice
    });
  };

  const confirmBulkGenerateInvoice = async () => {
    setBulkActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/customer-orders/bulk/generate-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ orderIds: Array.from(selectedOrders) })
      });
      const data = await response.json();

      if (data.success) {
        let message = data.message;
        if (data.errors?.length > 0) {
          message += `. Errors: ${data.errors.map(e => e.error).join(', ')}`;
        }
        setAlertDialog({ isOpen: true, message, type: data.errors?.length > 0 ? 'warning' : 'success' });
        setSelectedOrders(new Set());
        searchOrders(pagination.page);
      } else {
        throw new Error(data.error || 'Failed to generate invoices');
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to generate invoices: ${error.message}`, type: 'error' });
    }
    setBulkActionLoading(false);
  };

  // ============================================================
  // Helpers
  // ============================================================

  const { error, warning, success } = STATUS_COLORS;

  // An order can be deleted while it's still draft: no linked PO sent or
  // confirmed, not despatched/delivered, not billed, not invoiced. Child splits
  // can be deleted unless invoiced (their qty is restored to the parent).
  // Deleting an order with draft POs triggers the PO-deletion checklist.
  const canDeleteOrder = (order) => {
    if (order.is_split) return order.invoiced_status !== 'Invoiced';
    return (
      order.invoiced_status !== 'Invoiced' &&
      (parseInt(order.po_billed_count) || 0) === 0 &&
      (order.delivery_status || 'Not Despatched') === 'Not Despatched' &&
      (order.sent_status || 'No POs Sent') === 'No POs Sent' &&
      (order.confirmed_status || 'No POs Confirmed') === 'No POs Confirmed'
    );
  };

  const getStatusIcons = pipelineIconsForOrder;
  const getBilledInfo = billedIconForOrder;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // dd/mm/yy, matching Search POs and Search Bills. "27 Aug 2026" needs half
  // again the width for the same information, and these two screens are read
  // side by side often enough that two date formats is a papercut.
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  // The order whose purchase orders are being sent. SendToSupplierFlow decides
  // what that means — one PO opens the email modal, several hand over to Search
  // Purchase Orders — so both menus and the modal can share one entry point.
  const [sendFlowOrder, setSendFlowOrder] = useState(null);

  /*
   * Raise a third-party order's PO-H from its row — for an order saved without
   * one ("Not now" on the order screen, or a third-party split). Then offer to
   * send it to the haulier, the same way the order screen does.
   */
  const raiseHaulagePO = (order) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Raise haulage order?',
      message: `Raise the haulage order (PO-H) for ${order.order_number}${order.haulier_name ? ` with ${order.haulier_name}` : ''}?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const response = await fetch(`${API_URL}/api/orders/customer-orders/${order.id}/create-haulage-po`, {
            method: 'POST',
            credentials: 'include'
          });
          const result = await response.json();
          if (!response.ok || !result.success) throw new Error(result.error || 'Failed to raise the haulage order');
          searchOrders(pagination.page);
          const po = result.purchase_order;
          setConfirmDialog({
            isOpen: true,
            title: 'Send to haulier?',
            message: `${result.message}. Send it to ${po.haulier || 'the haulier'} now?`,
            onConfirm: () => {
              setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
              setSendFlowOrder({
                ...order,
                linked_pos: [{
                  id: po.id,
                  purchase_order_number: po.number,
                  xero_purchase_order_id: po.xero_purchase_order_id,
                  sent_to_contact: po.sent_to_contact,
                  supplier_name: po.haulier
                }]
              });
            }
          });
        } catch (error) {
          setAlertDialog({ isOpen: true, type: 'error', message: error.message });
        }
      }
    });
  };

  /**
   * Archive or restore one order. The list re-reads afterwards rather than
   * patching the row in place: an archived order leaves the active view
   * entirely, so the row it was in should not still be sitting there.
   */
  const archiveDone = ({ type, message }) => {
    setArchiveOrderFlow(null);
    setAlertDialog({ isOpen: true, type, message });
    if (type !== 'error') searchOrders(pagination.page);
  };

  /*
   * Archive or restore one order. Archiving runs ArchiveOrderFlow — the same
   * sequence the planning boards use: the checklist of its PO-Ds and PO-H, a
   * cancellation email for each one a supplier or haulier already holds, sent
   * first, then everything archives together. Restoring is one call.
   */
  const handleArchiveOrder = async (order, archived) => {
    if (archived) { setArchiveOrderFlow({ order }); return; }
    try {
      const data = await patchOrderArchive(order.id, false);
      archiveDone(orderArchiveOutcome(order, false, data));
    } catch (error) {
      setAlertDialog({ isOpen: true, type: 'error', message: error.message });
    }
  };

  // Shared by the row's RowActionsMenu and the right-click context menu, so the
  // two surfaces can never drift apart.
  const buildRowActions = (order) => {
    const isChildSplit = order.is_split;
    const canDelete = canDeleteOrder(order);
    // Delivered and invoiced orders open for corrections too (Cam, 2026-10-09 —
    // utils/afterDelivery); the server refuses what may no longer change.
    const canCreatePO = order.po_status === 'No POs Linked';
    // Delivered and not yet invoiced. Pricing can be finished on the invoice.
    const invoiceBlock = invoiceBlockFor(order);
    // Not before the delivery day, nor — where POs drive it — before it is
    // despatched; stock-only and third-party orders are exempt from the second
    // (utils/suppliedQty, mirrored by the server).
    // From our stock: the haulier's collection is recorded before the date.
    const collects = collectsFromStock(order);
    const supplyBlock = suppliedQtyBlock({
      deliveryDate: order.delivery_date,
      deliveryStatus: order.delivery_status,
      terms: order.excel_agri_delivery_terms,
      hasSupplyPos: (order.linked_pos || []).length > 0,
      collects
    });
    const canAddSuppliedQty = !supplyBlock;
    const hasSuppliedQty = order.has_supplied_qty;
    // A draft invoice waiting in Price Orders bills the whole order (utils/orderHub).
    const canSplit = canAddSuppliedQty && hasSuppliedQty && !order.is_split && !order.has_splits && !order.draft_invoice_no;

    return [
      {
        key: 'view',
        readSafe: true,   // opens what the caller can already see
        icon: 'visibility',
        label: 'View Order',
        title: `Open ${order.order_number}`,
        onClick: () => handleViewOrder(order.id)
      },
      // A rep asks the office to change it — a request, not an edit.
      ...(canSendRequests ? [(() => {
        const block = orderAmendBlock(order);
        return {
          key: 'request-change',
          readSafe: true,
          icon: 'edit_note',
          label: 'Request a change',
          disabled: !!block,
          title: block || 'Ask the office to change this order',
          onClick: () => { if (!block) setChangeRequestOrderId(order.id); }
        };
      })()] : []),
      {
        key: 'create-po',
        icon: 'shopping_cart',
        label: 'Create PO',
        title: canCreatePO ? "Create PO" : "Cannot create PO — POs already linked",
        onClick: () => {
          if (canCreatePO) {
            handleCreatePO(order);
          } else {
            setAlertDialog({ isOpen: true, message: `A purchase order can only be created when no POs are linked. Current PO status: "${order.po_status}".`, type: 'info' });
          }
        }
      },
      {
        key: 'send-supplier',
        icon: 'forward_to_inbox',
        label: 'Send to Supplier',
        disabled: !(order.linked_pos || []).length,
        title: (order.linked_pos || []).length
          ? 'Send to Supplier'
          : 'Cannot send — no purchase orders raised against this order',
        onClick: () => {
          if ((order.linked_pos || []).length) setSendFlowOrder(order);
        }
      },
      // A third-party haulier's order: status and supplied quantities are
      // recorded together, whatever the dates say (AddSuppliedQuantityModal).
      order.excel_agri_delivery_terms === 'Third-Party Delivery' ? {
        key: 'supplied-qty',
        icon: 'local_shipping',
        label: collects ? 'Collection / Delivery' : 'Update Delivery',
        className: order.delivery_status === 'Delivered' && hasSuppliedQty ? 'supplied-set' : undefined,
        // Invoiced is no longer the end (2026-10-08): until the invoice has
        // gone out to the customer it may change, and the window offers the
        // invoice changes; once it has, the window says so.
        disabled: !!supplyBlock,
        title: supplyBlock || (order.invoiced_status === 'Invoiced'
          ? 'Invoiced — it can still change until the invoice has gone out to the customer'
          : collects ? 'Record the haulier\'s collection (date and quantities) — it is delivered by its date after that'
            : 'Record delivery status and supplied quantities'),
        onClick: () => { if (!supplyBlock) setSuppliedQtyOrderId(order.id); }
      } : {
        key: 'supplied-qty',
        icon: 'inventory',
        label: canAddSuppliedQty ? (hasSuppliedQty ? "Update Supplied Qty" : "Add Supplied Qty") : "Supplied Qty",
        className: (canAddSuppliedQty && hasSuppliedQty) ? 'supplied-set' : undefined,
        disabled: !canAddSuppliedQty,
        title: canAddSuppliedQty
          ? (hasSuppliedQty ? "Update Supplied Qty" : "Add Supplied Qty")
          : `Not yet — ${supplyBlock}`,
        onClick: () => {
          if (!canAddSuppliedQty) {
            setAlertDialog({ isOpen: true, message: `Supplied quantities cannot be recorded yet: ${supplyBlock}.`, type: 'info' });
          } else {
            setSuppliedQtyOrderId(order.id);
          }
        }
      },
      {
        key: 'split',
        icon: 'call_split',
        label: 'Split Order',
        title: order.po_status === 'No POs Linked' ? "Cannot split — no PO created yet" :
          order.invoiced_status === 'Invoiced' ? "Cannot split — order is invoiced" :
          order.draft_invoice_no ? splitBlockForOrder(order) :
          order.is_split ? "Cannot split - this is a child split order" :
          order.has_splits ? "Cannot split - already has split orders" :
          !hasSuppliedQty ? "Add supplied quantity first" :
          "Split Order",
        onClick: () => {
          if (!canSplit) {
            const reason = order.invoiced_status === 'Invoiced'
              ? 'This order cannot be split because it has been invoiced.'
              : order.draft_invoice_no
              ? `${splitBlockForOrder(order)}.`
              : order.po_status === 'No POs Linked'
              ? 'A purchase order must be created before splitting an order.'
              : order.is_split
              ? 'This is a child split order and cannot be split again. Delete the child order first to re-split the parent.'
              : order.has_splits
              ? 'This order already has split orders. Delete existing child orders first to re-split.'
              : 'All order lines must have a supplied quantity set before splitting.';
            setAlertDialog({ isOpen: true, message: reason, type: 'info' });
          } else {
            setSplitOrderId(order.id);
          }
        }
      },
      /*
       * Divert: the WHOLE order to another customer when it cannot be
       * delivered as planned — numbers and links kept (Split sends part of
       * it). Whatever the delivery status; not once invoiced or archived.
       */
      (() => {
        const divertBlock = divertBlockForOrder(order);
        return {
          key: 'divert',
          icon: 'alt_route',
          label: 'Divert…',
          disabled: !!divertBlock,
          title: divertBlock || 'Divert — send the whole order to another customer',
          onClick: () => { if (!divertBlock) setDivertOrderId(order.id); }
        };
      })(),
      /*
       * Refused on delivery: where it is held and when it goes again — the
       * order and its PO-D stay as they are (utils/redelivery).
       */
      redeliverMenuAction(order, user, setRedeliverOrderId),
      /*
       * A split order's tonnage added to the customer's other order that day,
       * invoiced there as one line (utils/tonnageAdditions).
       */
      addToOrderMenuAction(order, user, setAddToOrderId),
      {
        key: 'invoice',
        icon: 'receipt',
        label: 'Create Invoice',
        title: invoiceBlock || 'Create Invoice — opens Create Invoice filled from this order',
        onClick: () => {
          if (invoiceBlock) {
            setAlertDialog({ isOpen: true, message: invoiceBlock, type: 'info' });
          } else {
            startInvoiceFromOrder(onNavigate, order.id);
          }
        }
      },
      {
        key: 'edit',
        icon: 'edit',
        label: 'Edit',
        title: orderEditTitle(order, { restricted: !canDelete }),
        onClick: () => handleEditOrder(order.id)
      },
      {
        key: 'duplicate',
        icon: 'content_copy',
        label: 'Duplicate',
        title: 'Start a new order pre-filled from this one',
        onClick: () => handleDuplicateOrder(order.id)
      },
      /*
       * Archive, where Delete used to be.
       *
       * Deleting an order destroys the record of something that happened, and
       * the reason people reached for it was almost always "get it out of my
       * list". Archiving does that and keeps the order, so it has none of
       * Delete's conditions: a sent PO or a delivered order is exactly what
       * you would archive out of the active view. Restoring is the same
       * action again from the archived view.
       */
      {
        key: 'archive',
        icon: order.is_archived ? 'unarchive' : 'archive',
        label: order.is_archived ? 'Restore' : 'Archive',
        title: order.is_archived
          ? 'Put this order back in the active list'
          : 'Move this order out of the active list. Nothing is lost.',
        onClick: () => handleArchiveOrder(order, !order.is_archived)
      },
    ];
  };

  return (
    <div className="search-customer-orders">
      {/* The rep phone shell hides .search-header entirely — see RepMobile.css
          — and this stands in for it. Desktop renders nothing. */}
      <RepMobileFilterReset onReset={handleClearFilters} />
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search, add, and manage customer orders</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>View order details, add orders, edit/ update orders, invoice, bill, and track order fulfillment</span>
          </span>
          {/* Sales orders are not the live source yet — orders.customer_orders
              is empty and everything real is still raised in Xero as a purchase
              order. Say so here rather than let an empty screen read as "your
              customers have not ordered anything". Remove at cutover. */}
          <span className="note cutover-note">
            <span className="material-symbols-rounded">pending_actions</span>
            <span style={{ margin: 'auto 0' }}>
              Sales Orders are not in use yet — until cutover is complete, look
              in Purchase Orders and Invoicing for live order information.
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            {!isRep && (
              <div>
                {/* Held open while requests are waiting, so the count is
                    read without a hover; a round button when none are. */}
                <button
                  className={`btn-slideout ror-count-host${repRequestCount.count ? ' always-out has-requests' : ''}`}
                  data-read-safe
                  onClick={() => setShowRepRequests(true)}
                  disabled={!repRequestCount.ready}
                  title={!repRequestCount.ready ? REP_REQUESTS_NOT_READY
                    : (repRequestCount.count ? `${repRequestCount.count} request${repRequestCount.count === 1 ? '' : 's'} from reps waiting` : 'Requests from reps — none waiting')}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">assignment_ind</span>
                  </div>
                  <span className="btn-label">Rep requests{repRequestCount.count ? ` (${repRequestCount.count})` : ''}</span>
                </button>
              </div>
            )}
            <div>
              <ExportTableButton
                build={buildExport}
                user={user}
                rowCount={cols.rows.length}
                onError={(message) => setAlertDialog({ isOpen: true, message, type: 'error' })}
              />
            </div>
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button data-write-action onClick={handleAddOrder} className="btn-slideout" disabled={loading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group so-filter-search">
            <label>
              Search
              <SearchHelp
                amounts
                fields={['order no.', 'customer', 'account no.', 'notes', 'product', 'item code', 'supplier', 'additives', 'PO no. (PO-D / PO-H)', 'supplier ref.', 'haulier', 'total']}
              />
            </label>
            <div className="search-with-additives">
              <Input
                type="text"
                placeholder='Search orders...'
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onEnter={() => searchOrders(1)}
                icon="search"
              />
              <TickCheckbox
                id="so-include-additives"
                checked={filters.includeAdditives}
                onChange={(e) => handleFilterChange('includeAdditives', e.target.checked)}
                label="+ADD"
                className="include-additives-pill"
                ariaLabel="Include additives"
              />
            </div>
          </div>

          {/* Supplier and Our Delivery Terms have gone from the row. The
              Supplier/s column now carries every supplier on the order and
              right-clicking its header narrows on them across the whole result
              set, which the single-value picker could not do. */}
          <ProductRangeFilter
            side="purchase"
            value={filters.productRange}
            onChange={(value) => handleFilterChange('productRange', value)}
          />

          <DateRangeFilter
            from={filters.dateFrom}
            to={filters.dateTo}
            preset={filters.datePreset}
            onChange={({ from, to, preset }) => setFilters(prev => ({
              ...prev, dateFrom: from, dateTo: to, datePreset: preset, page: 1
            }))}
          />

          <div className="filter-group so-filter-datetype" style={{ margin: '0 15px 0 -10px' }}>
            <label>Date Type</label>
            <Dropdown
              value={filters.dateType}
              onChange={(value) => handleFilterChange('dateType', value)}
              options={[
                { value: 'delivery', label: 'Delivery' },
                { value: 'created', label: 'Created' }
              ]}
              placeholder="Select..."
            />
          </div>

          {/* Archived rows are never mixed in with live ones — one or the
              other. A row somebody has put away is a row they would otherwise
              act on by mistake. Same control and same place as Search Haulage
              Jobs. */}
          <div className="filter-group so-filter-archived" style={{ margin: '0 -6px 0 -20px' }}>
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="so-archived"
                  type="checkbox"
                  checked={!!filters.archived}
                  onChange={(e) => handleFilterChange('archived', e.target.checked)}
                />
                <label htmlFor="so-archived"></label>
              </div>
            </div>
          </div>

          <div className="filter-group so-filter-limit">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="20"
            />
          </div>
        </div>

        {/* The six status dropdowns have gone. They took a whole second
            filter row to ask six yes/no/partly questions one value at a
            time; right-clicking the Status header now asks all six, as a
            selection rather than a single value, and narrows the whole
            result set rather than the page. */}
      </div>

      <div className="orders-panel">
        {/* Bulk Actions Bar */}
        {bulkActionsVisible && (
          <div className={`bulk-actions-bar ${bulkActionsClosing ? 'closing' : ''}`}>
            <div className="bulk-actions-info">
              <span className="bulk-actions-title">Bulk Actions:</span>
              <label className="tick-check-label" style={{ gap: '0' }}>
                <input type="checkbox" className="tick-check-input" id="bulk-select-indicator" checked={true} readOnly />
                <label htmlFor="bulk-select-indicator" className="tick-check">
                  <svg width="16px" height="16px" viewBox="0 0 18 18">
                    <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                    <polyline points="1 9 7 14 15 4"></polyline>
                  </svg>
                </label>
              </label>
              <span>{selectedOrders.size} item{selectedOrders.size !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="bulk-actions-buttons slideout">
              <div>
                <button data-write-action
                  className="btn-slideout"
                  onClick={handleBulkSuppliedQty}
                  disabled={bulkActionLoading}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">inventory</span>
                  </div>
                  <span className="btn-label">Add Supplied Qty</span>
                </button>
              </div>
              <div>
                <button data-write-action
                  className="btn-slideout"
                  onClick={handleBulkGenerateInvoice}
                  disabled={bulkActionLoading}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">receipt</span>
                  </div>
                  <span className="btn-label">Generate Invoice</span>
                </button>
              </div>
              <div>
                <button data-write-action
                  className="btn-slideout btn-danger"
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">delete</span>
                  </div>
                  <span className="btn-label">Delete Selected</span>
                </button>
              </div>
              <div>
                <button data-read-safe
                  className="btn-slideout"
                  onClick={() => setSelectedOrders(new Set())}
                  disabled={bulkActionLoading}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">close</span>
                  </div>
                  <span className="btn-label">Clear Selection</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="table-toolbar">
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
            <button
              className="btn-secondary btn-sm"
              onClick={handleExpandAll}
              disabled={loading || orders.length === 0}
            >
              <span className="material-symbols-rounded">
                {expandAllMode ? 'unfold_less' : 'unfold_more'}
              </span>
              {expandAllMode ? 'Collapse All' : 'Expand All'}
            </button>
            <ResetButton />
          </div>
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />
          {pagination && pagination.total > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={changePage}
            />
          )}
        </div>

        {/* Wrapped so the horizontal cue can sit still against the
            visible edge — inside the scrolling box it would travel
            with the columns and leave the screen. */}
        <div className="scroll-more-wrap">
        <div className="table-container scroll-more-box" ref={tableScrollRef}>
          {/* A rep's phone: one card per order instead of the table (Cam,
              28 Oct 2026 — RepOrderCard says why). The desktop table below
              is untouched; the two never render together. */}
          {repMobile && (
            <div className="rep-order-cards">
              {loading ? (
                <LoadingSpinner />
              ) : cols.rows.length === 0 ? (
                <div className="rep-order-cards-empty">No orders found</div>
              ) : (
                cols.rows.map((order) => (
                  <RepOrderCard
                    key={order.id}
                    order={order}
                    items={itemsOf(order)}
                    suppliers={suppliersOf(order)}
                    lineRows={orderLineRowsOf(order)}
                    expanded={expandedRows.has(order.id)}
                    onOpenOrder={() => handleViewOrder(order.id)}
                    onToggleLines={() => toggleRowExpand(order.id)}
                    onOpenCustomer={order.account_id ? () => setContactModalId(order.account_id) : null}
                    onOpenPurchaseOrder={(po) => openPurchaseOrder(po)}
                    onRequestChange={canSendRequests ? () => setChangeRequestOrderId(order.id) : null}
                  />
                ))
              )}
            </div>
          )}
          {!repMobile && (
          <table className="data-table resizable-table table-min-lg" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="select-all-orders"
                      checked={orders.length > 0 && selectedOrders.size === orders.length}
                      onChange={toggleSelectAll}
                      disabled={orders.length === 0}
                    />
                    <label htmlFor="select-all-orders" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="expand-col"><ResizeHandle colIndex={1} /></th>
                <th className="sortable" onClick={() => handleSort('order_number')}>
                  Order # {sortField === 'order_number' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                {/* Third-party haulage order — its own column, so the status
                    icons column does not take on yet another icon. */}
                <th className={`no-sort po-h-col ${cols.headerProps('third_party').className || ''}`.trim()}
                    onContextMenu={cols.headerProps('third_party').onContextMenu}
                    title="Right-click to filter third-party orders">
                  PO-H #
                  <ResizeHandle colIndex={3} />
                </th>
                {/* The cell shows an account number and a company, and people
                    order by both — so the click offers the choice rather than
                    picking one. */}
                <th className={`sortable ${cols.headerProps('company_name').className || ''}`.trim()}
                    onClick={sortMenu.headerProps('company_name').onClick}
                    onContextMenu={cols.headerProps('company_name').onContextMenu}
                    title="Click to choose how to sort · right-click to filter">
                  Customer {['company_name', 'account_no'].includes(sortField) && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                {/* Both read off the order's linked purchase orders, so neither
                    is sortable: the server orders customer orders, and a column
                    built from a nested list has no single value to sort on. */}
                <th className={`no-sort ${cols.headerProps('supplier_names').className || ''}`.trim()}
                    onContextMenu={cols.headerProps('supplier_names').onContextMenu}
                    title="Right-click to filter by supplier">
                  Supplier/s
                  <ResizeHandle colIndex={5} />
                </th>
                <th className={`no-sort ${cols.headerProps('item_names').className || ''}`.trim()}
                    onContextMenu={cols.headerProps('item_names').onContextMenu}
                    title="Right-click to filter by item">
                  Item/s
                  <ResizeHandle colIndex={6} />
                </th>
                <th className="sortable" onClick={() => handleSort('order_date')}>
                  Created {sortField === 'order_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th className={`sortable ${cols.headerProps('delivery_date').className || ''}`.trim()}
                    onClick={() => handleSort('delivery_date')}
                    onContextMenu={cols.headerProps('delivery_date').onContextMenu}
                    title="Right-click to filter by the delivery dates on this page">
                  Delivery {sortField === 'delivery_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={8} />
                </th>
                <th className="sortable" style={{ textAlign: 'right' }} onClick={() => handleSort('total_amount')}>
                  Total {sortField === 'total_amount' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={9} />
                </th>
                <th className={`no-sort ${cols.headerProps('status').className || ''}`.trim()}
                    style={{ textAlign: 'center' }}
                    onContextMenu={cols.headerProps('status').onContextMenu}
                    title="Right-click to filter by any of the statuses — POs, sent, confirmed, transport, delivery, supplied qty, billed, invoiced, priced">
                  Status
                  <ResizeHandle colIndex={10} />
                </th>
                <th className="actions-col no-sort"><ResizeHandle colIndex={11} /></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : cols.rows.length === 0 ? (
                <tr>
                  <td colSpan="12" className="no-results">No orders found</td>
                </tr>
              ) : (
                cols.rows.map((order) => {
                  const isSelected = selectedOrders.has(order.id);
                  const isExpanded = expandedRows.has(order.id);
                  const suppliers = suppliersOf(order);
                  const items = itemsOf(order);
                  const lineRows = orderLineRowsOf(order);
                  const lineCount = lineRows ? lineRows.length : 0;

                  return (
                    <React.Fragment key={order.id}>
                    <tr
                      className={`clickable-row expandable-row ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected' : ''} ${order.is_archived ? 'row-archived' : ''} ${poSentRowClass(order.linked_pos)}`}
                      onClick={() => toggleRowExpand(order.id)}
                      onContextMenu={ctxMenu.onRowContextMenu(order)}
                    >
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <label
                          className="tick-check-label"
                          style={{ justifyContent: 'center', gap: '0' }}
                          onMouseDown={dragSelect.onMouseDown(order.id, isSelected)}
                          onMouseEnter={dragSelect.onMouseEnter(order.id)}
                          onClick={(e) => e.preventDefault()}
                        >
                          <input
                            type="checkbox"
                            className="tick-check-input"
                            id={`select-order-${order.id}`}
                            checked={isSelected}
                            onChange={() => toggleSelectOrder(order.id)}
                          />
                          <label htmlFor={`select-order-${order.id}`} className="tick-check">
                            <svg width="16px" height="16px" viewBox="0 0 18 18">
                              <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                              <polyline points="1 9 7 14 15 4"></polyline>
                            </svg>
                          </label>
                        </label>
                      </td>
                      {/* An indicator, not the control: the whole row toggles,
                          as it does on Search Purchase Orders. */}
                      <td
                        className="expand-cell"
                        title={lineCount
                          ? (isExpanded ? "Hide the order's lines" : "Show the order's lines")
                          : 'No order lines to show'}
                      >
                        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}${lineCount ? '' : ' empty'}`}>
                          ▼
                        </span>
                      </td>
                      <td data-label="Order" className="order-col">
                        <span className="order-number-display">
                          <DivertedBadge
                            variant="icon"
                            diversions={order.diversions}
                            orderId={order.id}
                            orderNumber={order.order_number}
                            onChanged={() => searchOrders(pagination.page)}
                          />
                          <RedeliveryBadge
                            variant="icon"
                            redelivery={order.redelivery}
                            orderId={order.id}
                            onChanged={() => searchOrders(pagination.page)}
                          />

                          {(order.has_splits || order.is_split) && (
                            <span
                              className="linked-icon"
                              title={order.is_split ? `Split from ${order.parent_order_number}` : 'Has linked split orders'}
                            >
                              <span className="material-symbols-rounded">link</span>
                            </span>
                          )}
                          {/* The way into the order, now that the row itself
                              expands. Styled as a link so it reads as one —
                              the same shape the PO number carries on Search
                              Purchase Orders. */}
                          <span
                            className="order-number-link"
                            title={`Open ${order.order_number}`}
                            onClick={(e) => { e.stopPropagation(); handleViewOrder(order.id); }}
                          >
                            {order.order_number}
                          </span>
                        </span>
                        <NonInventoryBadge
                          present={order.non_inventory_line_count > 0}
                          count={order.non_inventory_line_count}
                          variant="icon"
                        />
                        {/* A split's tonnage invoiced on the customer's other
                            order: "Added to SO-D…" / "Includes 1.94 T from SO-D…A";
                            a click opens the other order. */}
                        <TonnageAdditionBadge
                          variant="compact"
                          additions={order.tonnage_additions}
                          orderId={order.id}
                          onOpenOrder={(id) => handleViewOrder(id)}
                          onChanged={() => searchOrders(pagination.page)}
                        />
                        {/* Every linked PO is listed — an order's lines can sit
                            across more than one PO, so showing only the first
                            would imply a link that isn't the whole picture. */}
                        {Array.isArray(order.linked_pos) && order.linked_pos.length > 0 && (
                          <div className="linked-po-list">
                            {order.linked_pos.map((linkedPo) => (
                              <span
                                key={linkedPo.id}
                                className="linked-po-link"
                                title={`Open ${linkedPo.purchase_order_number}`}
                                onClick={(e) => { e.stopPropagation(); openPurchaseOrder(linkedPo); }}
                              >
                                {linkedPo.purchase_order_number}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td data-label="PO-H" className="po-h-cell">
                        <HaulageCell
                          order={order}
                          onOpen={(po) => openPurchaseOrder(po)}
                          onRaise={() => raiseHaulagePO(order)}
                        />
                      </td>
                      <td className="customer-col">
                        <span className="stacked-cell">
                          <span className="name-with-badge">
                            {order.account_no && (
                              <span
                                className="account-badge-inline"
                                style={order.account_id ? { cursor: 'pointer' } : undefined}
                                onClick={order.account_id ? (e) => { e.stopPropagation(); setContactModalId(order.account_id); } : undefined}
                              >
                                {order.account_no}
                              </span>
                            )}
                            {order.company_name ? (
                              <span
                                className="contact-link"
                                onClick={(e) => { e.stopPropagation(); if (order.account_id) setContactModalId(order.account_id); }}
                              >
                                {order.company_name}
                              </span>
                            ) : (
                              <span>-</span>
                            )}
                          </span>
                          {/* Where it is going. Two customers of the same name
                              are told apart by the farm, and a driver's
                              question is almost always about the address. */}
                          {addressLine1(order.address) && (
                            <span className="cell-subline">{addressLine1(order.address)}</span>
                          )}
                        </span>
                      </td>
                      <td data-label="Supplier/s" className="suppliers-col">
                        {suppliers.length === 0 ? (
                          <span className="muted-dash">-</span>
                        ) : (
                          <span className="stacked-cell">
                            {suppliers.map((supplier) => (
                              supplier.id ? (
                                <span
                                  className="contact-link supplier-name"
                                  key={supplier.id}
                                  onClick={(e) => { e.stopPropagation(); setContactModalId(supplier.id); }}
                                >
                                  {supplier.name}
                                </span>
                              ) : (
                                <span className="supplier-name" key={`name:${supplier.name}`}>{supplier.name}</span>
                              )
                            ))}
                          </span>
                        )}
                      </td>
                      <td data-label="Item/s" className="items-col">
                        {items.length === 0 ? (
                          <span className="muted-dash">-</span>
                        ) : (
                          <span className="stacked-cell">
                            {items.map((item) => (
                              <span className="name-with-badge" key={item.key}>
                                {item.code && <span className="account-badge-inline">{item.code}</span>}
                                <span>
                                  {item.name}
                                  {item.quantity ? ` (${formatQuantity(item.quantity)})` : ''}
                                </span>
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td data-label="Created" className="created-col">{formatDate(order.order_date)}</td>
                      <td data-label="Delivery" className="delivery-col">
                        {formatDate(order.delivery_date)}
                        {/* Third-party from our stock: the day the haulier collected it. */}
                        {order.collected_date && <span className="collected-date-sub" title={collectedText(order.collected_date)}>Collected {formatDate(order.collected_date)}</span>}
                      </td>
                      <td data-label="Total" className="currency total-col">{formatCurrency(order.total_amount)}</td>
                      <td data-label="Status" className="status-col">
                        {(() => {
                          const icons = getStatusIcons(order);
                          const billed = getBilledInfo(order);
                          const pricedColor = order.is_priced === 'Priced' ? success : order.is_priced === 'Partially Priced' ? warning : error;
                          const pricedTitle = order.is_priced || 'Not Priced';
                          return (
                            <div className="status-icons">
                              {order.is_archived && <span className="archived-badge">Archived</span>}
                              <span className="material-symbols-rounded" style={{ color: icons.cart.color }} title={icons.cart.title}>shopping_cart</span>
                              <span className="material-symbols-rounded" style={{ color: icons.mail.color }} title={icons.mail.title}>mail</span>
                              <span className="material-symbols-rounded" style={{ color: icons.confirm.color }} title={icons.confirm.title}>check_circle</span>
                              <span className="material-symbols-rounded" style={{ color: icons.truck.color }} title={icons.truck.title}>local_shipping</span>
                              <span className="material-symbols-rounded" style={{ color: icons.pkg.color }} title={icons.pkg.title}>package_2</span>
                              <span className="material-symbols-rounded" style={{ color: billed.color }} title={billed.title}>receipt_long</span>
                              <span className="material-symbols-rounded" style={{ color: icons.receipt.color }} title={icons.receipt.title}>receipt</span>
                              <span className="material-symbols-rounded" style={{ color: pricedColor }} title={pricedTitle}>verified</span>
                              {order.has_delivered_qty_updates && (
                                <span className="delivered-qty-badge" title="Delivered quantities updated">(d)</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu actions={buildRowActions(order)} />
                      </td>
                    </tr>
                    {/* One row per ORDER line (Cam, 23 Oct 2026): what the
                        customer ordered, each with the PO it is bought on — a
                        stock line has none. The PO's own lines are on Search
                        Purchase Orders. */}
                    <tr className={`expanded-content ${isExpanded ? 'expanded' : ''}`}>
                      <td colSpan="12">
                        <div className="line-items-container">
                          {lineRows && lineRows.length > 0 ? (
                            <table className={`line-items-table so-line-items-table${seeCosts ? ' with-cost' : ''}`}>
                              <thead>
                                <tr>
                                  <th className="ol-item">Item</th>
                                  <th className="ol-desc">Description</th>
                                  <th className="ol-supplier">Supplier</th>
                                  <th className="ol-po">PO#</th>
                                  <th className="ol-qty text-right">Qty</th>
                                  <th className="ol-supplied text-right" title="What was supplied — recorded on the order line (Add / Update Supplied Qty)">Supplied qty</th>
                                  {seeCosts && (
                                    <th className="ol-cost text-right" title="The line's unit cost: its own, else the item's per the line's unit — with its additives">Cost</th>
                                  )}
                                  <th className="ol-sale text-right" title="The sale price per unit (what the invoice charges)">Sale</th>
                                  <th className="ol-vat text-right">VAT £ (%)</th>
                                  <th className="ol-status">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lineRows.map(({ key, line, po }) => {
                                  const additives = Array.isArray(line.additives) ? line.additives : [];
                                  const costDiffers = seeCosts && po && line.po_unit_cost != null && line.unit_cost != null
                                    && Math.abs(Number(line.po_unit_cost) - Number(line.unit_cost)) > 0.005;
                                  const sale = Number(line.unit_price);
                                  return (
                                  <tr
                                    key={key}
                                    /* A line on a PO: sent or not — red or
                                       nothing, as the PO is. A stock line has
                                       nothing to send. */
                                    className={`so-line-row${po ? ` has-po ${poSentRowClass([po])}` : ''}`}
                                    onContextMenu={po ? poLineCtxMenu.onRowContextMenu({ key, po, line, order }) : undefined}
                                    title={po ? `Right-click for ${po.purchase_order_number}` : undefined}
                                  >
                                    {/* The item code above the catalogue name;
                                        the line's own words are beside it, in
                                        Description. */}
                                    <td className="ol-item">
                                      <ItemCodeDescription
                                        stacked
                                        code={line.item_code}
                                        itemId={line.item_id}
                                        description={line.item_name || (line.item_code ? '' : line.sale_description)}
                                      />
                                      {additives.length > 0 && (
                                        <span className="cell-subline ol-additives" title="Additives on this line">
                                          + {additives.map(a => a.name).filter(Boolean).join(' + ')}
                                        </span>
                                      )}
                                    </td>
                                    {/* Sale over purchase, with Create Customer
                                        Order's icons — read only here. */}
                                    <td className="ol-desc">
                                      <LineWordings
                                        sale={line.sale_description}
                                        purchase={line.purchase_description || (line.is_stock ? 'From our stock' : '')}
                                        purchaseMuted={line.is_stock}
                                        purchaseTitle={line.is_stock
                                          ? 'From our stock: nothing is bought for this line'
                                          : (line.purchase_from_po && po
                                            ? `Purchase description: the words on ${po.purchase_order_number}, and on the supplier's bill`
                                            : undefined)}
                                      />
                                    </td>
                                    <td className="ol-supplier">
                                      <span className="stacked-cell">
                                        {line.is_stock ? (
                                          <>
                                            <span className="supplier-name">Stock</span>
                                            {line.supplier_name && <span className="cell-subline">{line.supplier_name}</span>}
                                          </>
                                        ) : (
                                          <span className="supplier-name">{line.supplier_name || '-'}</span>
                                        )}
                                        {/* The supplier's OWN reference for
                                            the order — what they will quote
                                            back at us. Text, not a link. */}
                                        {po?.supplier_reference && (
                                          <span className="cell-subline" title="The supplier's reference for this order">REF: {po.supplier_reference}</span>
                                        )}
                                      </span>
                                    </td>
                                    <td className="ol-po">
                                      {po ? (
                                        <span
                                          className="linked-po-link"
                                          title={`Open ${po.purchase_order_number}`}
                                          onClick={(e) => { e.stopPropagation(); openPurchaseOrder(po); }}
                                        >
                                          {po.purchase_order_number}
                                        </span>
                                      ) : ''}
                                    </td>
                                    {/* Ordered and supplied, each in the order
                                        line's own unit with the tonnes beneath. */}
                                    <td className="text-right ol-qty"><QtyWithTonnes quantity={line.quantity} line={line} /></td>
                                    <td className="text-right ol-supplied">
                                      {line.supplied_quantity != null
                                        ? <QtyWithTonnes quantity={line.supplied_quantity} line={line} pallets={null} />
                                        : (order.delivery_status === 'Delivered' && order.invoiced_status !== 'Invoiced'
                                          ? <span className="ol-supplied-needed" title="No supplied quantity recorded: until one is, the order is invoiced at the quantity ordered. Record what went before invoicing.">Not recorded — needed before invoicing</span>
                                          : '')}
                                    </td>
                                    {seeCosts && (
                                      <td className="text-right ol-cost">
                                        <span className="stacked-cell">
                                          <span>{line.unit_cost == null ? '-' : <PriceWithUnit value={line.unit_cost} line={line} />}</span>
                                          {line.additive_cost != null && line.additive_cost !== 0 && (
                                            <span className="cell-subline" title="The additives' prices, included in the cost">
                                              incl. {formatPrice(line.additive_cost)} additives
                                            </span>
                                          )}
                                          {costDiffers && (
                                            <span className="cell-subline ol-cost-po" title={`The cost on ${po.purchase_order_number}'s line`}>
                                              PO {withPerUnit(formatPrice(line.po_unit_cost), priceFactsOf(line))}
                                            </span>
                                          )}
                                        </span>
                                      </td>
                                    )}
                                    <td className="text-right ol-sale" title={sale > 0 ? undefined : 'Not priced yet'}>
                                      {sale > 0 ? <PriceWithUnit value={sale} line={line} /> : '-'}
                                    </td>
                                    <td className="text-right ol-vat">{formatVatWithRate(line.tax_amount, line.tax_type, formatCurrency)}</td>
                                    <td className="ol-status">
                                      {po ? (
                                        <>
                                          <span className={poStatusBadgeClass(po.status)}>
                                            {poStatusLabel(po.status)}
                                          </span>
                                          {(po.xero_purchase_order_id?.toString().startsWith('TEMP-')
                                            || po.xero_purchase_order_id?.toString().startsWith('LOCAL-')) && (
                                            <span
                                              className="material-symbols-rounded po-line-status-icon local-only"
                                              title="Local only — not synced to Xero"
                                            >
                                              link_off
                                            </span>
                                          )}
                                          {po.sent_to_contact === true && (
                                            <span className="material-symbols-rounded po-line-status-icon sent" title="Sent to supplier">mail</span>
                                          )}
                                          {po.sent_to_contact === false && (
                                            <span className="material-symbols-rounded po-line-status-icon unsent" title="Not sent to supplier">unsubscribe</span>
                                          )}
                                          {po.sent_to_contact === null && (
                                            <span className="material-symbols-rounded po-line-status-icon unknown" title="Sent status unknown (not synced)">mail</span>
                                          )}
                                        </>
                                      ) : line.is_stock ? (
                                        <span className="ol-status-plain" title="From our stock: nothing is bought for this line">Stock</span>
                                      ) : (
                                        <span className="ol-status-plain muted" title="Not on a purchase order yet">No PO yet</span>
                                      )}
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <div className="no-lines">
                              {lineRows === null
                                ? "Could not load this order's lines — search again to retry."
                                : 'This order has no lines.'}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
          )}
        </div>
          <ScrollMore scrollRef={tableScrollRef} remaining={tableScroll.remainingX} direction="right" />
        </div>
      </div>

      {sendFlowOrder && (
        <SendToSupplierFlow
          linkedPos={sendFlowOrder.linked_pos}
          orderNumber={sendFlowOrder.order_number}
          user={user}
          onSent={(message) => {
            setAlertDialog({ isOpen: true, message, type: 'success' });
            // The row's unsent marker is computed from linked_pos, so it only
            // changes once the list is re-read.
            searchOrders(pagination.page);
          }}
          onOpenInPOSearch={(orderNumber) => openOrderInPOSearch(orderNumber)}
          onClose={() => setSendFlowOrder(null)}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        }}
        onCancel={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}
      />

      {deleteOrderData && (
        <DeleteOrderModal
          order={deleteOrderData.order}
          linkedPOs={deleteOrderData.linkedPOs}
          loading={deleteLoading}
          onConfirm={(deletePoIds) => confirmDeleteOrder(deleteOrderData.order.id, deletePoIds)}
          onCancel={() => setDeleteOrderData(null)}
        />
      )}

      {selectedOrderId && ReactDOM.createPortal(
        <CustomerOrderModal
          user={user}
          onOpenOrderInPOSearch={openOrderInPOSearch}
          orderId={selectedOrderId}
          onClose={() => { setSelectedOrderId(null); searchOrders(pagination.page); }}
          onEdit={handleEditOrder}
          onNavigate={onNavigate}
        />,
        document.body
      )}

      {poModalXeroId && (
        <PurchaseOrderModal
          user={user}
          purchaseOrderId={poModalXeroId}
          onClose={() => { setPoModalXeroId(null); searchOrders(pagination.page); }}
          onOpenInSearch={onNavigate ? openPOInSearch : undefined}
        />
      )}

      <AddSuppliedQuantityModal
        user={user}
        isOpen={!!suppliedQtyOrderId}
        orderId={suppliedQtyOrderId}
        onClose={() => setSuppliedQtyOrderId(null)}
        onSuccess={() => {
          setSuppliedQtyOrderId(null);
          searchOrders(pagination.page);
        }}
      />

      <SplitOrderModal
        isOpen={!!splitOrderId}
        orderId={splitOrderId}
        onClose={() => setSplitOrderId(null)}
        onSuccess={() => {
          setSplitOrderId(null);
          searchOrders(pagination.page);
        }}
      />

      {divertOrderId && (
        <DivertOrderFlow
          orderId={divertOrderId}
          user={user}
          onClose={() => { setDivertOrderId(null); searchOrders(pagination.page); }}
        />
      )}

      {addToOrderId && (
        <AddToOrderModal
          orderId={addToOrderId}
          onClose={() => { setAddToOrderId(null); searchOrders(pagination.page); }}
          onDone={(message) => message && setAlertDialog({ isOpen: true, message, type: 'success' })}
        />
      )}

      {redeliverOrderId && (
        <RedeliverOrderModal
          orderId={redeliverOrderId}
          onClose={() => { setRedeliverOrderId(null); searchOrders(pagination.page); }}
          onDone={(message) => message && setAlertDialog({ isOpen: true, message, type: 'success' })}
        />
      )}

      {contactModalId && (
        <ContactModal
          contactId={contactModalId}
          onClose={() => setContactModalId(null)}
        />
      )}

      {archiveOrderFlow && (
        <ArchiveOrderFlow
          key={archiveOrderFlow.order.id}
          order={archiveOrderFlow.order}
          user={user}
          onDone={archiveDone}
          onClose={() => setArchiveOrderFlow(null)}
        />
      )}

      {/* Table context menu (right-click) */}
      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={(ids) => [
          {
            key: 'bulk-supplied-qty',
            icon: 'inventory',
            label: `Add supplied qty to ${ids.size} orders`,
            disabled: bulkActionLoading,
            onClick: handleBulkSuppliedQty
          },
          {
            key: 'bulk-invoice',
            icon: 'receipt',
            label: `Generate invoices for ${ids.size} orders`,
            disabled: bulkActionLoading,
            onClick: handleBulkGenerateInvoice
          },
          {
            key: 'bulk-delete',
            icon: 'delete',
            label: `Delete ${ids.size} orders`,
            disabled: bulkActionLoading,
            onClick: handleBulkDelete
          }
        ]}
        actions={[
          {
            key: 'select-all',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'select_all',
            label: selectedOrders.size === orders.length && orders.length > 0 ? 'Clear selection' : 'Select all',
            disabled: orders.length === 0,
            onClick: toggleSelectAll
          },
          {
            key: 'refresh',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'refresh',
            label: 'Refresh results',
            disabled: loading,
            onClick: () => searchOrders(pagination.page)
          },
          {
            key: 'clear-filters',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: handleClearFilters
          }
        ]}
      />

      {/* Right-click inside an expanded row. The row is one purchase order, so
          "Send to Supplier" here needs no disambiguation — the supplier has
          already been picked by the row that was clicked, which is the one
          thing the order-level action cannot do when an order is split across
          several mills. */}
      <TableContextMenu
        ctx={poLineCtxMenu}
        rowActions={(target) => [
          {
            key: 'view-po',
            readSafe: true,
            icon: 'visibility',
            label: `View ${target.po.purchase_order_number}`,
            disabled: !target.po.xero_purchase_order_id,
            title: target.po.xero_purchase_order_id
              ? `Open ${target.po.purchase_order_number}`
              : 'Cannot open — this purchase order has no Xero reference',
            onClick: () => openPurchaseOrder(target.po)
          },
          {
            key: 'send-po',
            icon: 'forward_to_inbox',
            label: 'Send to Supplier',
            title: `Send ${target.po.purchase_order_number} to ${target.po.supplier_name || 'its supplier'}`,
            onClick: () => setSendFlowOrder({
              order_number: target.order.order_number,
              linked_pos: [target.po]
            })
          },
          {
            key: 'po-in-search',
            readSafe: true,
            icon: 'search',
            label: 'Find in Search Purchase Orders',
            disabled: !onNavigate,
            onClick: () => openPOInSearch({ purchaseOrderNumber: target.po.purchase_order_number })
          }
        ]}
      />
      <ColumnFilterMenu cols={cols} />

      {showRepRequests && (
        <RepOrderRequestsModal
          onClose={() => { setShowRepRequests(false); loadRepRequestCount(); }}
          onNavigate={onNavigate}
          onChanged={(open) => { if (open !== null && open !== undefined) setRepRequestCount(c => ({ ...c, count: open })); }}
        />
      )}
      {changeRequestOrderId && (
        <RepChangeRequestModal
          kind="amend_order"
          targetId={changeRequestOrderId}
          onClose={() => setChangeRequestOrderId(null)}
        />
      )}

      <SortOptionsMenu
        menu={sortMenu}
        menuKey="company_name"
        title="Sort by customer"
        fields={[
          { value: 'account_no', label: 'Account number' },
          { value: 'company_name', label: 'Customer name' }
        ]}
        field={sortField}
        direction={sortDirection}
        labels={{ asc: 'A to Z / low to high', desc: 'Z to A / high to low' }}
        onChange={(field, direction) => { setSortField(field); setSortDirection(direction); }}
      />
    </div>
  );
}

export default SearchCustomerOrders;
